import { Check, ChevronDown, ChevronLeft, ChevronRight, Download, Share2, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  formatearPrecio,
  type MetodoPrecio,
} from '@postmaker/shared';
import {
  aplicarColoresMarca,
  colorDeMarca,
  cuadrarProductos,
  LIENZOS,
  recentrarBloqueIncompleto,
  repartirEnBloques,
  zPosterElement,
  type Formato,
  type PosterElement,
  type PosterResuelto,
} from '@postmaker/poster';
import { api } from '../lib/api.ts';
import { registrarFuentes, type FuenteCargable } from '../lib/fuentes.ts';
import { leerSeleccion, nombreFormato, useProductosCatalogo, type ProductoCatalogo } from '../lib/catalogo.ts';
import { useSesion } from '../lib/sesion.ts';
import {
  abrirWhatsApp,
  copiarImagen,
  armarZip,
  compartirArchivos,
  descargarArchivo,
  elegirCarpeta,
  esMovil,
  estrategiaDisponible,
  guardarEnCarpeta,
} from '../lib/descargas.ts';
import { LienzoEditor } from '../componentes/editor/LienzoEditor.tsx';
import { Aviso, Boton, BotonCirculo, Cargando, Casilla, Chip, EncabezadoPagina, Selector } from '../componentes/ui.tsx';
import { PasosPost } from '../componentes/PasosPost.tsx';

interface PlantillaDetalle {
  id: string;
  nombre: string;
  numProductos: number;
  assets: Record<string, string>;
  lienzos: {
    formato: Formato;
    fondoColor: string | null;
    fondoUrl: string | null;
    elementos: unknown[];
  }[];
}

interface PrecioGuardado {
  productoId: string;
  metodo: MetodoPrecio;
  valorMetodo: number | null;
  precioAhora: number;
  precioAntes: number | null;
  estilo: 'SIMPLE' | 'ANTES_AHORA';
}

interface Logo {
  id: string;
  nombre: string;
  url: string;
}
interface Contacto {
  id: string;
  canal: string;
  valor: string;
  principal: boolean;
}

interface ItemLote {
  indice: number;
  url: string | null;
  referencias: string[];
  descargado: boolean;
}

/**
 * Editor del cliente (F5). Móvil-excelente (D31, D37): Pointer Events, bottom sheet,
 * snapping agresivo. En modo masivo TODO cambio afecta a todos los posts (D35): hay un
 * solo estado de elementos y N bloques de productos.
 */
export function PaginaEditorCliente() {
  const { t } = useTranslation();
  const busqueda = useSearch({ strict: false }) as { plantillaId?: string };
  const plantillaId = busqueda.plantillaId ?? '';
  const qc = useQueryClient();
  const { data: sesion } = useSesion();

  const seleccion = useMemo(() => leerSeleccion(), []);
  const productoIds = useMemo(() => seleccion?.productoIds ?? [], [seleccion]);

  const plantilla = useQuery({
    queryKey: ['plantilla-cliente', plantillaId],
    queryFn: () => api<PlantillaDetalle>(`/catalogo/plantillas/${plantillaId}`),
    enabled: Boolean(plantillaId),
  });

  // El panel ya cargó el catálogo; se reusa para armar los datos reales del preview.
  const catalogo = useProductosCatalogo({});
  const productosPorId = useMemo(() => {
    const mapa = new Map<string, ProductoCatalogo>();
    for (const pagina of catalogo.data?.pages ?? []) {
      for (const p of pagina.items) mapa.set(p.id, p);
    }
    return mapa;
  }, [catalogo.data]);
  // Cargar todo el catálogo visible (para selecciones grandes).
  useEffect(() => {
    if (catalogo.hasNextPage && !catalogo.isFetchingNextPage) void catalogo.fetchNextPage();
  }, [catalogo]);

  const logos = useQuery({
    queryKey: ['perfil-logos'],
    queryFn: () => api<{ items: Logo[] }>('/perfil/logos'),
  });
  const contactos = useQuery({
    queryKey: ['perfil-contactos'],
    queryFn: () => api<{ items: Contacto[] }>('/perfil/contactos'),
  });
  const fuentesEmpresa = useQuery({
    queryKey: ['catalogo-fuentes'],
    queryFn: () =>
      api<{ items: { id: string; familia: string; peso: number; italic: boolean; url: string | null }[] }>(
        '/catalogo/fuentes',
      ),
    staleTime: 5 * 60 * 1000,
  });
  const fuentesCargables: FuenteCargable[] = useMemo(
    () =>
      (fuentesEmpresa.data?.items ?? []).map((f) => ({
        fuenteId: f.id,
        familia: f.familia,
        url: f.url,
        peso: f.peso,
        italic: f.italic,
      })),
    [fuentesEmpresa.data],
  );
  useEffect(() => {
    void registrarFuentes(fuentesCargables);
  }, [fuentesCargables]);

  const marcaArteId = productosPorId.get(productoIds[0] ?? '')?.marcaId ?? null;
  const logosMarca = useQuery({
    queryKey: ['marca-logos-cliente', marcaArteId],
    queryFn: () =>
      api<{ items: { assetId: string | null; nombre: string; url: string | null }[] }>(
        `/catalogo/marcas/${marcaArteId}/logos`,
      ),
    enabled: Boolean(marcaArteId),
    staleTime: 5 * 60 * 1000,
  });

  const precios = useQuery({
    queryKey: ['precios', productoIds.join(',')],
    queryFn: () =>
      api<{ items: PrecioGuardado[] }>(`/precios?productoIds=${productoIds.join(',')}`),
    enabled: productoIds.length > 0,
  });
  const preciosPorId = useMemo(
    () => new Map((precios.data?.items ?? []).map((p) => [p.productoId, p])),
    [precios.data],
  );

  // ── Estado del editor. Persistencia en sesión: un refresh no borra el trabajo (D25). ──
  const claveSesion = `pm_editor_${plantillaId}`;
  const [formato, setFormato] = useState<Formato>('R4_5');
  const [elementos, setElementos] = useState<PosterElement[]>([]);
  const [fondoColor, setFondoColor] = useState<string | null>(null);
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [logoClienteId, setLogoClienteId] = useState<string | null>(null);
  /** Variante del logo de marca elegida (assetId de MarcaLogo); null = principal. */
  const [logoMarcaAssetId, setLogoMarcaAssetId] = useState<string | null>(null);
  const [elementosSeleccionados, setElementosSeleccionados] = useState<string[]>([]);
  const [bloqueActivo, setBloqueActivo] = useState(0);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [cargado, setCargado] = useState(false);
  const historial = useRef<PosterElement[][]>([]);

  const lienzo = plantilla.data?.lienzos.find((l) => l.formato === formato);

  // Carga inicial: sesión guardada o plantilla.
  useEffect(() => {
    if (!plantilla.data || cargado) return;
    const guardado = sessionStorage.getItem(claveSesion);
    if (guardado) {
      try {
        const estado = JSON.parse(guardado) as {
          formato: Formato;
          elementos: PosterElement[];
          fondoColor: string | null;
          textos: Record<string, string>;
          logoClienteId: string | null;
          logoMarcaAssetId?: string | null;
        };
        setFormato(estado.formato);
        setElementos(zPosterElement.array().parse(estado.elementos));
        setFondoColor(estado.fondoColor);
        setTextos(estado.textos);
        setLogoClienteId(estado.logoClienteId);
        setLogoMarcaAssetId(estado.logoMarcaAssetId ?? null);
        setCargado(true);
        return;
      } catch {
        sessionStorage.removeItem(claveSesion);
      }
    }
    const primero = plantilla.data.lienzos[0];
    if (primero) {
      setFormato(primero.formato);
      const parseados = zPosterElement.array().safeParse(primero.elementos);
      setElementos(parseados.success ? parseados.data : []);
      setFondoColor(primero.fondoColor);
    }
    setCargado(true);
  }, [plantilla.data, cargado, claveSesion]);

  // Persistencia continua en sessionStorage.
  useEffect(() => {
    if (!cargado) return;
    sessionStorage.setItem(
      claveSesion,
      JSON.stringify({ formato, elementos, fondoColor, textos, logoClienteId, logoMarcaAssetId }),
    );
  }, [cargado, claveSesion, formato, elementos, fondoColor, textos, logoClienteId, logoMarcaAssetId]);

  function cambiarFormato(nuevo: Formato) {
    const lienzoNuevo = plantilla.data?.lienzos.find((l) => l.formato === nuevo);
    if (!lienzoNuevo) return;
    setFormato(nuevo);
    const parseados = zPosterElement.array().safeParse(lienzoNuevo.elementos);
    setElementos(parseados.success ? parseados.data : []);
    setFondoColor(lienzoNuevo.fondoColor);
    setElementosSeleccionados([]);
    historial.current = [];
  }

  const cambiarEnVivo = useCallback(
    (nuevos: PosterElement[]) => setElementos(cuadrarProductos(nuevos, LIENZOS[formato])),
    [formato],
  );
  const apilar = useCallback((previo: PosterElement[]) => {
    historial.current.push(previo);
    if (historial.current.length > 60) historial.current.shift();
  }, []);

  function deshacer() {
    const previo = historial.current.pop();
    if (previo) setElementos(previo);
  }

  // ── Bloques (D23) y datos resueltos por bloque ────────────────────────────
  const numProductos = plantilla.data?.numProductos ?? 1;
  const bloques = useMemo(
    () => repartirEnBloques(productoIds, numProductos),
    [productoIds, numProductos],
  );

  const resueltoDe = useCallback(
    (bloque: string[]): PosterResuelto => {
      const productos: PosterResuelto['productos'] = [null, null, null];
      const presentes = new Set<number>();
      bloque.forEach((id, i) => {
        const p = productosPorId.get(id);
        if (!p) return;
        const precio = preciosPorId.get(id);
        const elPrecio = elementos.find((e) => e.tipo === 'precio' && e.indice === i);
        const opciones =
          elPrecio?.tipo === 'precio'
            ? { prefijo: elPrecio.prefijo, decimales: elPrecio.decimales }
            : {};
        productos[i] = {
          referencia: p.referencia,
          nombre: p.nombre,
          imagenUrl: p.imagenUrl,
          precio: precio
            ? {
                ahora: formatearPrecio(precio.precioAhora, opciones),
                // El precio tachado (antes) se quitó del producto final.
                antes: null,
              }
            : null,
        };
        presentes.add(i);
      });

      // El último bloque puede venir incompleto: recentrado (D36), igual que el servidor.
      let elementosBloque =
        bloque.length < numProductos ? recentrarBloqueIncompleto(elementos, presentes) : elementos;

      // Colores y logo de la marca del arte (la del primer producto del bloque).
      const marcaSel = sesion?.marcas.find(
        (m) => m.id === productosPorId.get(bloque[0] ?? '')?.marcaId,
      );
      const coloresMarca = {
        primario: marcaSel?.colorPrimario ?? '#111225',
        secundario: marcaSel?.colorSecundario ?? '#d7f75b',
      };
      elementosBloque = aplicarColoresMarca(elementosBloque, coloresMarca);

      return {
        formato,
        fondo: { color: colorDeMarca(fondoColor, coloresMarca), imagenUrl: lienzo?.fondoUrl ?? null },
        elementos: elementosBloque,
        productos,
        logoMarcaUrl:
          (logoMarcaAssetId
            ? logosMarca.data?.items.find((l) => l.assetId === logoMarcaAssetId)?.url
            : null) ?? marcaSel?.logoUrl ?? null,
        logoClienteUrl: logoClienteId
          ? (logos.data?.items.find((l) => l.id === logoClienteId)?.url ?? null)
          : (logos.data?.items[0]?.url ?? null),
        nombreNegocio: sesion?.cliente?.nombreNegocio ?? sesion?.cliente?.razonSocial ?? '',
        contactos: (contactos.data?.items ?? []).map((c) => ({ canal: c.canal, valor: c.valor })),
        textos,
        fuentes: fuentesCargables,
        // Assets de los elementos imagen + foto base de PERSONALIZAR_IMAGEN (D34).
        assets: {
          ...(plantilla.data?.assets ?? {}),
          ...(seleccion?.materialUrl ? { __imagenBase: seleccion.materialUrl } : {}),
        },
      };
    },
    [productosPorId, preciosPorId, elementos, numProductos, formato, fondoColor, lienzo, logoClienteId, logoMarcaAssetId, logosMarca.data, logos.data, contactos.data, sesion, textos, fuentesCargables, plantilla.data, seleccion],
  );

  // ── Generación y descarga (D29, D30, D39) ────────────────────────────────
  const [items, setItems] = useState<ItemLote[]>([]);
  const [generando, setGenerando] = useState(false);
  const [progreso, setProgreso] = useState({ listos: 0, total: 0 });
  const descargadosRef = useRef(new Set<number>());

  // Aviso antes de salir con imágenes sin descargar (§6.5, protección contra pérdida).
  useEffect(() => {
    function antesDeSalir(ev: BeforeUnloadEvent) {
      const pendientes = items.some((i) => i.url && !i.descargado);
      if (generando || pendientes) ev.preventDefault();
    }
    window.addEventListener('beforeunload', antesDeSalir);
    return () => window.removeEventListener('beforeunload', antesDeSalir);
  }, [items, generando]);

  function specBase() {
    return {
      plantillaId,
      formato,
      bloques,
      elementos,
      fondoColor: fondoColor ?? undefined,
      logoClienteId: logoClienteId ?? logos.data?.items[0]?.id ?? null,
      logoMarcaAssetId,
      textos,
      materialId: seleccion?.materialId,
    };
  }

  async function marcarDescargado(indice: number) {
    descargadosRef.current.add(indice);
    setItems((previos) => previos.map((i) => (i.indice === indice ? { ...i, descargado: true } : i)));
  }

  async function bajarBlob(url: string): Promise<Blob> {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`descarga ${resp.status}`);
    return resp.blob();
  }

  const nombreDe = (item: ItemLote) => `post-${item.referencias.join('-') || item.indice + 1}.png`;

  async function descargarItem(item: ItemLote, estrategia = estrategiaDisponible()) {
    if (!item.url) return;
    const blob = await bajarBlob(item.url);
    if (estrategia === 'carpeta' && (await guardarEnCarpeta(nombreDe(item), blob))) {
      await marcarDescargado(item.indice);
      return;
    }
    if (estrategia === 'compartir') {
      const ok = await compartirArchivos([new File([blob], nombreDe(item), { type: 'image/png' })]);
      if (ok) await marcarDescargado(item.indice);
      return;
    }
    descargarArchivo(nombreDe(item), blob);
    await marcarDescargado(item.indice);
  }

  const generar = useMutation({
    mutationFn: async () => {
      setGenerando(true);
      setItems([]);
      descargadosRef.current.clear();

      const estrategia = estrategiaDisponible();
      // Chrome/Edge: la carpeta se elige UNA vez, antes de arrancar (§6.5).
      const conCarpeta = estrategia === 'carpeta' && bloques.length > 1 ? await elegirCarpeta() : false;

      if (bloques.length === 1) {
        const r = await api<{ url: string; referencias: string[] }>('/render', {
          metodo: 'POST',
          cuerpo: { spec: { ...specBase(), bloques } },
        });
        const item: ItemLote = { indice: 0, url: r.url, referencias: r.referencias, descargado: false };
        setItems([item]);
        setProgreso({ listos: 1, total: 1 });
        await descargarItem(item);
        return;
      }

      const lote = await api<{ jobId: string; totalItems: number }>('/render/lote', {
        metodo: 'POST',
        cuerpo: { spec: specBase() },
      });
      setProgreso({ listos: 0, total: lote.totalItems });

      // Poll del progreso; cada imagen se baja apenas aparece (descarga progresiva, D30).
      const vistos = new Set<number>();
      for (let intento = 0; intento < 600; intento += 1) {
        const job = await api<{
          estado: string;
          itemsListos: number;
          totalItems: number;
          items: { indice: number; url: string | null; referencias: string[] }[];
          error: string | null;
        }>(`/render/jobs/${lote.jobId}`);

        setProgreso({ listos: job.itemsListos, total: job.totalItems });
        setItems(
          job.items.map((i) => ({
            ...i,
            descargado: descargadosRef.current.has(i.indice),
          })),
        );

        for (const item of job.items) {
          if (item.url && !vistos.has(item.indice)) {
            vistos.add(item.indice);
            // En móvil no se dispara share por cada imagen (rompería el gesto):
            // el usuario comparte por tandas con el botón al final.
            if (estrategia !== 'compartir') {
              void descargarItem({ ...item, descargado: false }, conCarpeta ? 'carpeta' : 'archivos');
            }
          }
        }

        if (job.estado === 'LISTO' || job.estado === 'ERROR') {
          if (job.error) setMensaje(`Error: ${job.error}`);
          break;
        }
        await new Promise((r) => setTimeout(r, 900));
      }
    },
    onError: (e) => setMensaje(`Error: ${e.message}`),
    onSettled: () => setGenerando(false),
  });

  async function compartirTanda() {
    const pendientes = items.filter((i) => i.url && !i.descargado).slice(0, 10);
    const archivos: File[] = [];
    for (const item of pendientes) {
      archivos.push(new File([await bajarBlob(item.url!)], nombreDe(item), { type: 'image/png' }));
    }
    if (archivos.length === 0) return;
    const ok = await compartirArchivos(archivos);
    if (ok) for (const item of pendientes) await marcarDescargado(item.indice);
  }

  async function descargarZip() {
    const listos = items.filter((i) => i.url);
    const entradas = [];
    for (const item of listos) {
      const blob = await bajarBlob(item.url!);
      entradas.push({ nombre: nombreDe(item), datos: new Uint8Array(await blob.arrayBuffer()) });
    }
    descargarArchivo('posts.zip', await armarZip(entradas));
    for (const item of listos) await marcarDescargado(item.indice);
  }

  function whatsappItem(item: ItemLote) {
    if (!item.url) return;
    void (async () => {
      const blob = await bajarBlob(item.url!);
      // 1) Hoja de compartir con el ARCHIVO (móvil y escritorios que la soportan).
      const compartido = await compartirArchivos(
        [new File([blob], nombreDe(item), { type: 'image/png' })],
        t('editor.textoWhatsApp'),
      );
      if (compartido) {
        await marcarDescargado(item.indice);
        return;
      }
      // 2) Escritorio: la imagen va al portapapeles y se abre WhatsApp Web — pegar la adjunta.
      const copiado = await copiarImagen(blob);
      if (!copiado) void descargarItem(item);
      abrirWhatsApp(t('editor.textoWhatsApp'));
      if (copiado) setMensaje(t('editor.imagenCopiada'));
    })();
  }

  function compartirItem(item: ItemLote) {
    if (!item.url) return;
    void (async () => {
      const blob = await bajarBlob(item.url!);
      const ok = await compartirArchivos([new File([blob], nombreDe(item), { type: 'image/png' })]);
      if (ok) {
        await marcarDescargado(item.indice);
        return;
      }
      // Sin hoja de compartir: copiar al portapapeles para pegar en cualquier red.
      if (await copiarImagen(blob)) setMensaje(t('editor.imagenCopiada'));
      else void descargarItem(item);
    })();
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (!seleccion || productoIds.length === 0) {
    return (
      <main className="p-8 text-center text-sm text-suave">
        {t('editor.sinSeleccion')}
      </main>
    );
  }
  if (plantilla.isPending || !cargado) return <Cargando texto={t('comun.cargando')} />;
  if (!plantilla.data || !lienzo) {
    return <main className="p-8 text-center text-sm text-suave">{t('editor.sinPlantilla')}</main>;
  }

  const dims = LIENZOS[formato];
  const escala = Math.min(1, (Math.min(430, window.innerWidth - 24) / dims.w) * 1.0, 620 / dims.h);
  const resueltoActivo = resueltoDe(bloques[Math.min(bloqueActivo, bloques.length - 1)] ?? []);
  const formatosDisponibles = plantilla.data.lienzos.map((l) => l.formato);
  const pendientes = items.filter((i) => i.url && !i.descargado).length;

  return (
    <main className="mx-auto w-full max-w-[1600px] pb-40">
      {/* El flujo manda: paso 3 grande arriba, ANTES del encabezado (sus chips cambian de ancho) */}
      <div className="flex justify-center py-2">
        <PasosPost paso={3} />
      </div>

      <EncabezadoPagina
        titulo={plantilla.data.nombre}
        atras={() => window.history.back()}
        acciones={
          <>
            {formatosDisponibles.map((f) => (
              <Chip key={f} activo={f === formato} onClick={() => cambiarFormato(f)}>
                {nombreFormato(f, t)}
              </Chip>
            ))}
            <BotonCirculo contorno aria-label="Deshacer" onClick={deshacer}><Undo2 size={16} /></BotonCirculo>
          </>
        }
      />

      {mensaje ? <Aviso>{mensaje}</Aviso> : null}

      <div className="flex flex-col items-center gap-4 xl:flex-row xl:items-start">
        {/* Textos editables (izquierda en escritorio, tras el lienzo en móvil) */}
        <aside className="order-2 w-full max-w-md xl:order-none xl:w-72 xl:shrink-0 xl:max-w-none">
          <PanelTextos
            elementos={elementos}
            textos={textos}
            onTexto={(binding, valor) => setTextos((previos) => ({ ...previos, [binding]: valor }))}
            onTextoLibre={(id, valor) =>
              setElementos((previos) =>
                previos.map((e) => (e.id === id && e.tipo === 'texto' ? { ...e, contenido: valor } : e)),
              )
            }
          />
        </aside>

        {/* Lienzo */}
        <section className="order-1 flex flex-col items-center gap-2 xl:order-none xl:flex-1">
          <LienzoEditor
            resuelto={resueltoActivo}
            elementos={resueltoActivo.elementos}
            onCambiar={cambiarEnVivo}
            onCommit={apilar}
            seleccion={elementosSeleccionados}
            onSeleccion={setElementosSeleccionados}
            escala={escala}
            modo="cliente"
          />
          {bloques.length > 1 ? (
            <div className="flex items-center gap-2 text-sm">
              <Boton
                variante="secundario"
                onClick={() => setBloqueActivo((b) => Math.max(0, b - 1))}
                disabled={bloqueActivo === 0}
              >
                <ChevronLeft size={16} />
              </Boton>
              <span>
                {t('editor.post')} {bloqueActivo + 1} / {bloques.length}
              </span>
              <Boton
                variante="secundario"
                onClick={() => setBloqueActivo((b) => Math.min(bloques.length - 1, b + 1))}
                disabled={bloqueActivo >= bloques.length - 1}
              >
                <ChevronRight size={16} />
              </Boton>
              <span className="text-xs text-suave">{t('editor.avisoMasivo')}</span>
            </div>
          ) : null}
        </section>

        {/* Resto de ajustes (derecha en escritorio) */}
        <section className="order-3 w-full max-w-md xl:order-none xl:w-[360px] xl:shrink-0 xl:max-w-none">
          <div className="space-y-3">
            {elementos.some((e) => e.opcional && !e.bloqueado) ? (
              <Plegable titulo={t('editor.opcionales')}>
                <div className="space-y-1.5">
                  {elementos
                    .filter((e) => e.opcional && !e.bloqueado)
                    .map((e) => (
                      <Casilla
                        key={e.id}
                        className="w-full"
                        activa={e.visible}
                        onCambiar={(v) =>
                          setElementos((previos) =>
                            previos.map((x) => (x.id === e.id ? { ...x, visible: v } : x)),
                          )
                        }
                        etiqueta={<span className="truncate">{e.nombre}</span>}
                      />
                    ))}
                </div>
              </Plegable>
            ) : null}

            <Plegable titulo={t('editor.precios')}>
              <TablaPrecios
                productoIds={productoIds}
                productosPorId={productosPorId}
                preciosPorId={preciosPorId}
                puedeVerCosto={sesion?.puedeVerCosto ?? false}
                alGuardar={() => qc.invalidateQueries({ queryKey: ['precios'] })}
              />
            </Plegable>

            <Plegable titulo={t('editor.fondo')}>
              <label className="flex items-center gap-3 text-sm">
                {t('editor.colorFondo')}
                <input
                  type="color"
                  value={fondoColor ?? '#ffffff'}
                  onChange={(e) => setFondoColor(e.target.value)}
                  className="campo"
                />
              </label>
            </Plegable>

            {elementos.some((e) => e.tipo === 'logoMarca') && (logosMarca.data?.items.length ?? 0) > 1 ? (
              <Plegable titulo={t('editor.logoMarca')}>
                <ul className="grid grid-cols-3 gap-2">
                  {logosMarca.data?.items.map((l) => (
                    <li key={l.assetId ?? 'principal'}>
                      <button
                        type="button"
                        onClick={() => setLogoMarcaAssetId(l.assetId)}
                        className="interno w-full bg-white p-1"
                        style={
                          logoMarcaAssetId === l.assetId
                            ? { outline: '2px solid var(--color-acento-var)', outlineOffset: 2 }
                            : undefined
                        }
                      >
                        {l.url ? <img src={l.url} alt={l.nombre} className="aspect-square w-full object-contain" /> : null}
                        <span className="block truncate text-center text-[10px] text-suave">{l.nombre}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </Plegable>
            ) : null}

            <Plegable titulo={t('editor.miLogo')}>
              {logos.data?.items.length === 0 ? (
                <p className="text-sm text-suave">{t('editor.sinLogos')}</p>
              ) : (
                <ul className="grid grid-cols-3 gap-2">
                  {logos.data?.items.map((l) => (
                    <li key={l.id}>
                      <button
                        type="button"
                        onClick={() => setLogoClienteId(l.id)}
                        className="interno w-full p-1"
                        style={
                          (logoClienteId ?? logos.data?.items[0]?.id) === l.id
                            ? { outline: '2px solid var(--color-acento-var)', outlineOffset: 2 }
                            : undefined
                        }
                      >
                        <img src={l.url} alt={l.nombre} className="aspect-square w-full object-contain" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Plegable>

            <Plegable titulo={t('editor.contactos')}>
              <div className="text-sm text-suave">
                {(contactos.data?.items ?? []).map((c) => (
                  <p key={c.id}>
                    <span className="font-semibold">{c.canal}</span> · {c.valor}
                  </p>
                ))}
                <p className="mt-2 text-xs">{t('editor.gestionarContactos')}</p>
              </div>
            </Plegable>
          </div>

          {/* Tira de miniaturas del lote + estado de descarga */}
          {items.length > 0 ? (
            <div className="tarjeta mt-4 p-4">
              <p className="mb-2 text-[15px] font-medium">
                {t('editor.progreso', { listos: progreso.listos, total: progreso.total })}
                {pendientes > 0 ? ` · ${t('editor.sinDescargar', { n: pendientes })}` : ''}
              </p>
              <ul className="flex gap-2 overflow-x-auto pb-1">
                {items.map((item) => (
                  <li key={item.indice} className="relative w-24 shrink-0">
                    {item.url ? (
                      <img src={item.url} alt="" className="aspect-[4/5] w-full rounded-lg object-cover" />
                    ) : (
                      <div className="flex aspect-[4/5] w-full animate-pulse items-center justify-center rounded-lg bg-superficie2 text-xs text-suave">
                        …
                      </div>
                    )}
                    {item.descargado ? (
                      <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
                        <Check size={11} />
                      </span>
                    ) : null}
                    {item.url ? (
                      <div className="mt-1 flex justify-center gap-1">
                        <button
                          type="button"
                          aria-label="Descargar"
                          className="circulo h-8 w-8 !bg-superficie2 !shadow-none"
                          onClick={() => void descargarItem(item)}
                        >
                          <Download size={13} />
                        </button>
                        <button
                          type="button"
                          aria-label="WhatsApp"
                          title="WhatsApp (con la foto)"
                          className="circulo h-8 w-8 !bg-emerald-500/15 !text-emerald-600 !shadow-none text-[10px] font-medium"
                          onClick={() => whatsappItem(item)}
                        >
                          WA
                        </button>
                        <button
                          type="button"
                          aria-label={t('editor.compartirGeneral')}
                          title={t('editor.compartirGeneral')}
                          className="circulo h-8 w-8 !bg-superficie2 !shadow-none"
                          onClick={() => compartirItem(item)}
                        >
                          <Share2 size={13} />
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
              {esMovil() && pendientes > 0 ? (
                <Boton className="mt-2 w-full" onClick={() => void compartirTanda()}>
                  {t('editor.compartirTanda', { n: Math.min(pendientes, 10) })}
                </Boton>
              ) : null}
              {!esMovil() && items.filter((i) => i.url).length > 20 ? (
                <Boton variante="secundario" className="mt-2 w-full" onClick={() => void descargarZip()}>
                  {t('editor.zip')}
                </Boton>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>

      {/* Barra fija inferior */}
      <div className="sticky bottom-4 z-30 mt-6">
        <div className="tarjeta flex items-center justify-between gap-3 px-5 py-3 shadow-[var(--sombra-alta)]">
          <p className="text-sm text-suave">
            {t('editor.resumen', { productos: productoIds.length, posts: bloques.length })}
          </p>
          <Boton onClick={() => generar.mutate()} disabled={generando}>
            {generando
              ? t('editor.generando', { listos: progreso.listos, total: progreso.total })
              : bloques.length > 1
                ? t('editor.generarLote', { n: bloques.length })
                : t('editor.generarUno')}
          </Boton>
        </div>
      </div>
    </main>
  );
}



/** Sección plegable de la barra lateral: abierta por defecto, toda la cabecera es tocable. */
function Plegable({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  const [abierta, setAbierta] = useState(true);
  return (
    <div className="tarjeta overflow-hidden">
      <button
        type="button"
        aria-expanded={abierta}
        onClick={() => setAbierta((a) => !a)}
        className="flex min-h-11 w-full items-center justify-between px-5 py-3 text-left"
      >
        <span className="text-[11px] font-medium uppercase tracking-wide text-suave">{titulo}</span>
        <ChevronDown
          size={16}
          className={`text-suave transition-transform ${abierta ? '' : '-rotate-90'}`}
        />
      </button>
      {abierta ? <div className="px-5 pb-5">{children}</div> : null}
    </div>
  );
}

// ── Panel de textos (barra izquierda): lo único que se toca aquí son las letras ──
function PanelTextos({
  elementos,
  textos,
  onTexto,
  onTextoLibre,
}: {
  elementos: PosterElement[];
  textos: Record<string, string>;
  onTexto: (binding: string, valor: string) => void;
  onTextoLibre: (id: string, valor: string) => void;
}) {
  const { t } = useTranslation();
  const deTexto = elementos.filter(
    (e): e is Extract<PosterElement, { tipo: 'texto' }> =>
      e.tipo === 'texto' && !e.bloqueado && e.visible,
  );
  // Un input por binding (titulo/subtitulo/insignia) presente en la plantilla…
  const bindings = [...new Set(deTexto.map((e) => e.binding))].filter(
    (b): b is 'titulo' | 'subtitulo' | 'insignia' =>
      b === 'titulo' || b === 'subtitulo' || b === 'insignia',
  );
  // …y un input por cada texto libre (el contenido se edita directo en el elemento).
  const libres = deTexto.filter((e) => e.binding === 'libre');

  return (
    <Plegable titulo={t('editor.textos')}>
      <div className="space-y-3">
      {bindings.length === 0 && libres.length === 0 ? (
        <p className="text-sm text-suave">{t('editor.sinTextos')}</p>
      ) : null}
      {bindings.map((binding) => (
        <label key={binding} className="block text-sm">
          <span className="mb-1 block font-medium text-suave">{t(`editor.${binding}`)}</span>
          <input
            className="campo min-h-11 w-full px-4"
            value={textos[binding] ?? ''}
            placeholder={
              deTexto.find((e) => e.binding === binding)?.contenido || t('editor.textoPlantilla')
            }
            onChange={(e) => onTexto(binding, e.target.value)}
          />
        </label>
      ))}
      {libres.map((e) => (
        <label key={e.id} className="block text-sm">
          <span className="mb-1 block font-medium text-suave">{e.nombre || t('editor.textoLibre')}</span>
          {/* textarea: los textos libres pueden traer saltos de línea de la plantilla */}
          <textarea
            className="campo w-full resize-none px-4 py-2.5"
            rows={Math.min(3, Math.max(1, e.contenido.split('\n').length))}
            value={e.contenido}
            onChange={(ev) => onTextoLibre(e.id, ev.target.value)}
          />
        </label>
      ))}
      </div>
    </Plegable>
  );
}

// ── Tabla de precios (SPEC §6.3): marcar → método+valor → aplicar a los marcados ──
function TablaPrecios({
  productoIds,
  productosPorId,
  preciosPorId,
  puedeVerCosto,
  alGuardar,
}: {
  productoIds: string[];
  productosPorId: Map<string, ProductoCatalogo>;
  preciosPorId: Map<string, PrecioGuardado>;
  puedeVerCosto: boolean;
  alGuardar: () => void;
}) {
  const { t } = useTranslation();
  const [marcados, setMarcados] = useState<Set<string>>(new Set(productoIds));
  const [metodo, setMetodo] = useState<MetodoPrecio>(puedeVerCosto ? 'PORCENTAJE' : 'MANUAL');
  const [valor, setValor] = useState('40');
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: (items: unknown[]) => api('/precios', { metodo: 'PUT', cuerpo: { items } }),
    onSuccess: () => {
      setError(null);
      alGuardar();
    },
    onError: (e) => setError(e.message),
  });

  function aplicarAMarcados() {
    const numero = Number(valor.replace(',', '.'));
    if (!Number.isFinite(numero)) return setError(t('precios.valorInvalido'));
    const items = [...marcados].map((productoId) => ({
      productoId,
      metodo,
      ...(metodo === 'MANUAL' ? { precioAhora: Math.round(numero * 100) / 100 } : { valorMetodo: numero }),
      estilo: 'SIMPLE',
    }));
    if (items.length > 0) guardar.mutate(items);
  }

  function editarAhora(productoId: string, texto: string) {
    const numero = Number(texto.replace(',', '.'));
    if (!Number.isFinite(numero) || numero <= 0) return;
    guardar.mutate([
      {
        productoId,
        metodo: 'MANUAL',
        precioAhora: Math.round(numero * 100) / 100,
        estilo: 'SIMPLE',
      },
    ]);
  }

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-end gap-2 text-sm">
        <Selector
          etiqueta={t('precios.metodo')}
          className="min-h-10 w-40 text-sm"
          value={metodo}
          onChange={(e) => setMetodo(e.target.value as MetodoPrecio)}
        >
            {puedeVerCosto ? (
              <>
                <option value="PORCENTAJE">{t('precios.porcentaje')}</option>
                <option value="MULTIPLICADOR">{t('precios.multiplicador')}</option>
                <option value="SUMA">{t('precios.suma')}</option>
              </>
            ) : null}
            <option value="MANUAL">{t('precios.manual')}</option>
        </Selector>
        <div className="w-24">
          <p className="mb-1.5 text-[13px] text-suave">{t('precios.valor')}</p>
          <input className="campo min-h-10 text-sm" value={valor} inputMode="decimal" onChange={(e) => setValor(e.target.value)} />
        </div>
        <Boton onClick={aplicarAMarcados} disabled={guardar.isPending}>
          {t('precios.aplicar', { n: marcados.size })}
        </Boton>
      </div>
      {error ? <Aviso>{error}</Aviso> : null}

      <div className="mb-1 flex gap-1.5">
        <Boton variante="texto" tamano="sm" onClick={() => setMarcados(new Set(productoIds))}>{t('precios.marcarTodos')}</Boton>
        <Boton variante="texto" tamano="sm" onClick={() => setMarcados(new Set())}>{t('precios.desmarcar')}</Boton>
      </div>

      <ul className="max-h-72 divide-y divide-superficie2 overflow-y-auto text-sm">
        {productoIds.map((id) => {
          const p = productosPorId.get(id);
          const precio = preciosPorId.get(id);
          if (!p) return null;
          return (
            <li key={id} className="flex items-center gap-2 py-1.5">
              <Casilla
                activa={marcados.has(id)}
                onCambiar={(v) => {
                  setMarcados((previos) => {
                    const nuevos = new Set(previos);
                    if (v) nuevos.add(id);
                    else nuevos.delete(id);
                    return nuevos;
                  });
                }}
              />
              <span className="w-24 shrink-0 font-mono text-xs">{p.referencia}</span>
              {puedeVerCosto && p.costo !== undefined ? (
                <span className="w-16 shrink-0 text-xs text-suave">${p.costo.toFixed(2)}</span>
              ) : null}
              <input
                key={precio?.precioAhora ?? 'sin'}
                className="campo min-h-9 w-24 rounded-xl px-3 text-right text-sm"
                defaultValue={precio ? precio.precioAhora.toFixed(2) : ''}
                placeholder={t('precios.sinPrecio')}
                inputMode="decimal"
                onBlur={(e) => {
                  if (e.target.value) editarAhora(id, e.target.value);
                }}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
